import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMCampusList from "../../features/university-manager/UMCampusList";
import UMCampusForm from "../../features/university-manager/UMCampusForm";
import AppButton from "../../components/ui/AppButton";
import PermissionGuard from "../../components/common/PermissionGuard";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";
import { useNotification } from "../../context/NotificationContext";
import useDebounce from "../../utils/useDebounce";

const UMCampuses = () => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();
  const { userRole, currentTenantId } = useTenant();
  const { showSuccess, showError } = useNotification();
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [editingCampus, setEditingCampus] = useState(null);
  const [statusFilter, setStatusFilter] = useState(true);

  const debouncedSearchTerm = useDebounce(searchTerm);

  const cache = useRef({});

  const invalidateCache = () => { cache.current = {}; };

  const fetchCampuses = useCallback(async () => {
    const key = `${statusFilter}|${currentPage}|${pageSize}|${debouncedSearchTerm}`;

    if (cache.current[key]) {
      const cached = cache.current[key];
      setCampuses(cached.content);
      setTotalPages(cached.totalPages);
      setTotalElements(cached.totalElements);
      return;
    }

    setLoading(true);
    const res = await umService.getCampuses({ userRole, currentTenantId, page: currentPage, size: pageSize, search: debouncedSearchTerm, status: statusFilter });
    if (res.success) {
      const data = { content: res.data.content, totalPages: res.data.totalPages, totalElements: res.data.totalElements };
      cache.current[key] = data;
      setCampuses(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    }
    setLoading(false);
  }, [userRole, currentTenantId, currentPage, pageSize, debouncedSearchTerm, statusFilter]);

  useEffect(() => { fetchCampuses(); }, [fetchCampuses]);

  const handleCreate = () => {
    setEditingCampus(null);
    setShowForm(true);
  };

  const handleEdit = (campus) => {
    setEditingCampus(campus);
    setShowForm(true);
  };

  const handleSave = async (data) => {
    setSaving(true);
    const res = editingCampus
      ? await umService.updateCampus({ id: editingCampus.id, userRole, currentTenantId, data })
      : await umService.createCampus({ userRole, currentTenantId, data });

    if (res.success) {
      showSuccess(t("crud.messages.saved"));
      setShowForm(false);
      setEditingCampus(null);
      invalidateCache();
      fetchCampuses();
    } else {
      showError(t("crud.messages.saveFailed"));
    }
    setSaving(false);
  };

  const handleDelete = async (campus) => {
    if (!window.confirm(t("crud.messages.confirmDelete"))) return;

    const res = await umService.deleteCampus({ id: campus.id, userRole, currentTenantId });
    if (res.success) {
      showSuccess(t("crud.messages.deleted"));
      invalidateCache();
      fetchCampuses();
      return;
    }

    if (res.message === "CAMPUS_HAS_DEPENDENCIES") {
      showError(t("crud.messages.campusHasDependencies"));
      return;
    }

    showError(t("crud.messages.deleteFailed"));
  };

  const handleReactivate = async (campus) => {
    if (!window.confirm(t("crud.messages.confirmReactivate"))) return;
    const res = await umService.updateCampus({
      id: campus.id,
      userRole,
      currentTenantId,
      data: { nameEn: campus.nameEn, nameLocal: campus.nameLocal, status: true },
    });
    if (res.success) {
      showSuccess(t("crud.messages.reactivated"));
      invalidateCache();
      fetchCampuses();
    } else {
      showError(t("crud.messages.reactivateFailed"));
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<AppButton key={i} variant={i === currentPage ? "primary" : "outline"} className="btn-sm mx-1" onClick={() => setCurrentPage(i)}>{i + 1}</AppButton>);
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">{tCommon("pagination.showingEntries", { start: currentPage * pageSize + 1, end: Math.min((currentPage + 1) * pageSize, totalElements), total: totalElements })}</div>
        <div className="d-flex align-items-center">
          <AppButton variant="outline" className="btn-sm me-2" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>{tCommon("buttons.previous")}</AppButton>
          {pages}
          <AppButton variant="outline" className="btn-sm ms-2" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages - 1}>{tCommon("buttons.next")}</AppButton>
        </div>
      </div>
    );
  };

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.campus.list)}>
      <div className="campuses-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("campuses.title")}</h1>
            <p className="text-muted mb-0">{t("campuses.subtitle")}</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0">{t("campuses.show")}:</label>
            <select className="form-select form-select-sm" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(0); }} style={{ width: "auto" }}>
              <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
            </select>
          </div>
        </div>
        <UMCampusList
          campuses={campuses}
          loading={loading}
          totalElements={totalElements}
          searchTerm={searchTerm}
          onSearchChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
          onClearSearch={() => { setSearchTerm(""); setCurrentPage(0); }}
          onCreate={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onReactivate={handleReactivate}
          statusFilter={statusFilter}
          onStatusFilterChange={(val) => { setStatusFilter(val); setCurrentPage(0); }}
        />
        {renderPagination()}
      </div>
      </PermissionGuard>

      {showForm && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCampus ? t("crud.editCampus") : t("crud.addCampus")}
                </h5>
                <button className="btn-close" onClick={() => setShowForm(false)} disabled={saving}></button>
              </div>
              <div className="modal-body">
                <UMCampusForm
                  initialData={editingCampus}
                  onSubmit={handleSave}
                  onCancel={() => setShowForm(false)}
                  loading={saving}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </UniversityManagerLayout>
  );
};

export default UMCampuses;
